import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FixedSizeList as List } from "react-window";
import { TempoElement } from "@/utils/tempoElement";
import { CssFieldValue } from ".";
import { JsxElement } from "@/utils/jsxElementTypes";
import {
  ClickableIcon,
  ComboboxContainer,
  ComboBoxOptionType,
  DiscreteInput,
  DiscreteOption,
  DropdownInput,
  DropdownOptionType,
  EmptyInputState,
} from "./TailwindStylingPanel/components/input";
import { VariantDraggableInput as DraggableInput } from "./TailwindStylingPanel/components/input/VariantDraggableInput";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  ArrowUp,
  ChevronsLeftRightIcon,
  ChevronsRightLeftIcon,
  Maximize2,
  Minimize2,
  MoveHorizontalIcon,
  Plus,
  Scan,
  SquareIcon,
  Type,
} from "lucide-react";
import {
  AlignContentCenterIcon,
  AlignContentFlexEndIcon,
  AlignContentFlexStartIcon,
  AlignContentSpaceAroundIcon,
  AlignContentSpaceBetweenIcon,
  AlignContentStretchIcon,
  AlignItemsBaseline,
  AlignItemsCenterIcon,
  AlignItemsEndIcon,
  AlignItemsStartIcon,
  AlignItemsStretchIcon,
  JustifyCenterIcon,
  JustifyFlexEndIcon,
  JustifyFlexStartIcon,
  JustifySpaceBetweenIcon,
  JustifySpaceEvenlyIcon,
  PlusIcon,
  MinusIcon,
  ExpandSettingPadding,
  SpacingIcon,
  FliPIcon,
  RevertIcon,
  SolidBarIcon,
  AlignLeftIcon,
} from "@/components/RightStyle/TailwindStylingPanel/components/icons";

import {
  AngleIcon,
  BlendingModeIcon,
  BorderAllIcon,
  BorderBottomIcon,
  BorderLeftIcon,
  BorderRightIcon,
  BorderSplitIcon,
  BorderStyleIcon,
  BorderTopIcon,
  CornerBottomLeftIcon,
  CornerBottomRightIcon,
  CornerTopLeftIcon,
  CornerTopRightIcon,
  CursorArrowIcon,
  FontSizeIcon,
  LetterSpacingIcon,
  LineHeightIcon,
  MagicWandIcon,
  OverlineIcon,
  ShadowIcon,
  StrikethroughIcon,
  TextAlignCenterIcon,
  TextAlignJustifyIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  UnderlineIcon,
} from "@radix-ui/react-icons";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import {
  type ClassType,
  type ClassTypeWithPropertyType,
  PropertyType,
  safeExtractValueFromCss,
} from "@/tailwind/tailwindUtils";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import {
  useEditingClassesByCodebaseId,
  useDesignCommandManager,
} from "@/store/zustand/designCommandManagerStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import _ from "lodash";
import { MaxWidthIcon } from "../Core/Icons";
import { MinWidthIcon } from "../Core/Icons";
import { cn } from "@/lib/utils";
import {
  calculateNewClasses,
  safeParseCssStringAsInlineStyles,
  sortTailwindColorOptions,
} from "./TailwindStylingPanel/utils";
import {
  modalTypeToTitle,
  type ModalState,
  StylingModalType,
} from "./TailwindStylingPanel/components/modals";
import CustomColorPicker from "./TailwindStylingPanel/components/input/CustomColorPicker";
import type React from "react";
import { isClassVariant, useNewPropertyState2 } from "./usePropertyStateV2";
import FillModalV2 from "./TailwindStylingPanel/components/modals/FillModalV2";
import "highlight.js/styles/github.css";
import { Command, CommandInput, CommandItem } from "../ui/command";
import ModalContainer from "./TailwindStylingPanel/components/modals/ModalContainer";
import { validHex } from "./TailwindStylingPanel/components/input/color-picker/utils/validate";
import { VariantControl } from "./TailwindStylingPanel/components/variant/VariantControl";
import AlignField from "./TailwindStylingPanel/fields/Layout/Align";
import { useSelector } from "react-redux";
import {
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
} from "@/store/iframeSlice";
import FlexDirectionWrapField from "./TailwindStylingPanel/fields/Layout/FlexDirectionWrap";
import AlignChildrenField from "./TailwindStylingPanel/fields/Layout/AlignChildren";
import WrapContentField from "./TailwindStylingPanel/fields/Layout/WrapContent";
import GapField from "./TailwindStylingPanel/fields/Layout/Gap";
import TextAlignField from "./TailwindStylingPanel/fields/Typography/TextAlign";
import TextDecorationField from "./TailwindStylingPanel/fields/Typography/TextDecoration";
import PropManager from "./PropManager";
import ColorSwatch from "./TailwindStylingPanel/components/input/ColorSwatch";

type EnhancedClassType = ClassType & {
  custom?: boolean;
  score: number;
};

const Row = ({
  index,
  style,
  filteredProperties,
  handleSelect,
}: {
  index: number;
  style: React.CSSProperties;
  filteredProperties: EnhancedClassType[];
  handleSelect: (cls: EnhancedClassType) => void;
}) => {
  const cls = filteredProperties[index];

  return (
    <CommandItem
      key={cls.tailwind}
      onSelect={() => {
        handleSelect(cls);
      }}
      className="!h-7"
      style={style}
    >
      <div className="h-full w-full">
        <div className="h-full w-full flex flex-row justify-between items-center gap-0.5">
          <div className="font-medium flex-shrink-0 nowrap space-x-2">
            <span className="text-text-primary text-xs font-normal">
              {cls.tailwind}
            </span>
          </div>
          <div className="flex flex-shrink-0 flex-col">
            <div className="flex flex-col h-full">
              <div className="leading-tight text-right flex-shrink w-fit text-text-tertiary text-xs font-normal line-clamp-1">
                {cls.css
                  .replace(/"/g, "")
                  .split(";")
                  // only take the first 2
                  .slice(0, 2)
                  .join(";")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommandItem>
  );
};

enum FlexGrowShrinkOptions {
  Grow = "Grow",
  Shrink = "Shrink",
  NoGrowShrink = "No Shrink / Grow",
}

const horizontalFlexGrowShrinkOptions: DropdownOptionType[] = [
  {
    label: "Grow if possible",
    value: FlexGrowShrinkOptions.Grow,
    icon: <ChevronsLeftRightIcon width={15} height={15} />,
  },
  {
    label: "Shrink if needed",
    value: FlexGrowShrinkOptions.Shrink,
    icon: <ChevronsRightLeftIcon width={15} height={15} />,
  },
  {
    label: "Don't shrink or grow",
    value: FlexGrowShrinkOptions.NoGrowShrink,
    icon: <MoveHorizontalIcon width={15} height={15} />,
  },
];

const verticalFlexGrowShrinkOptions: DropdownOptionType[] = [
  {
    label: "Grow if possible",
    value: FlexGrowShrinkOptions.Grow,
    icon: (
      <ChevronsLeftRightIcon width={15} height={15} className="rotate-90" />
    ),
  },
  {
    label: "Shrink if needed",
    value: FlexGrowShrinkOptions.Shrink,
    icon: <ChevronsRightLeftIcon width={15} height={15} />,
  },
  {
    label: "Don't shrink or grow",
    value: FlexGrowShrinkOptions.NoGrowShrink,
    icon: <MoveHorizontalIcon width={15} height={15} className="rotate-90" />,
  },
];

const BORDER_WIDTH_OPTIONS: DiscreteOption[] = [
  {
    key: "all",
    option: {
      css: "",
      tailwind: "",
    },
    labelContent: <BorderAllIcon />,
    tooltipContent: "All",
  },
  {
    key: "individual",
    option: {
      css: "",
      tailwind: "",
    },
    labelContent: <BorderSplitIcon />,
    tooltipContent: "Individual",
  },
];

const DebugInformation = ({
  cssValues,
  selectedElement,
  selectedTreeElement,
  elementClassList,
  parentCssEvals,
}: {
  cssValues: { [cssName: string]: CssFieldValue };
  selectedElement: TempoElement | null;
  selectedTreeElement: JsxElement | null;
  elementClassList: string[];
  parentCssEvals: { [cssName: string]: string };
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredCssValues = Object.entries(cssValues || {}).filter(([key]) =>
    key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="border-t border-gray-700 pt-4 mt-4">
      <h3 className="font-semibold text-lg mb-3 text-gray-100">
        Debug Information
      </h3>

      <div className="border-b border-gray-700 pb-3 mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Selected Element
        </h4>
        <div className="pl-2">
          <div className="flex items-center mb-1 text-sm">
            <span className="w-1/3 text-gray-400">Codebase ID:</span>
            <span className="w-2/3 font-medium text-gray-200">
              {selectedElement?.codebaseId}
            </span>
          </div>
          <div className="flex items-center text-sm">
            <span className="w-1/3 text-gray-400">Storyboard ID:</span>
            <span className="w-2/3 font-medium text-gray-200">
              {selectedElement?.storyboardId}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">Class List</h4>
        <div className="pl-2 break-words text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {elementClassList?.join(" ")}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Parent CSS Evaluations
        </h4>
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {parentCssEvals &&
            Object.entries(parentCssEvals).map(([key, value]) => (
              <div key={key} className="mb-1">
                <span className="text-gray-400">{key}:</span> {value}
              </div>
            ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Selected Tree Element
        </h4>
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {selectedTreeElement &&
            Object.entries(selectedTreeElement).map(([key, value]) => (
              <div key={key} className="mb-1">
                <span className="text-gray-400">{key}:</span>{" "}
                {JSON.stringify(value)}
              </div>
            ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full">
        <h4 className="font-semibold text-md mb-2 text-gray-200">CSS Values</h4>
        <input
          type="text"
          placeholder="Search CSS values..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-2 p-2 bg-gray-700 text-gray-200 rounded"
        />
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {filteredCssValues.map(([key, value]) => (
            <div key={key} className="mb-2">
              <span className="text-gray-400">{key}:</span>
              <div className="pl-2">
                <div>Evaluated Value: {value.evaluatedValue}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type Props = {
  parentCssEvals?: { [cssName: string]: string };
  selectedTreeElement: JsxElement | null;
  cssValues: { [cssName: string]: CssFieldValue };
};

const updateInstantForPropertyThrottled = _.throttle(
  ({
    propertyType,
    option,
    setTemporaryDraggingValue,
    variant,
    elementClasses,
    tailwindClassesMap,
    selectedElement,
  }: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
    elementClasses: string[];
    tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;
    selectedElement: TempoElement | null;
  }) => {
    let newClasses: string[];

    // Special handling for gradient color properties to preserve gradient structure
    if (
      [
        PropertyType.FROM_COLOR,
        PropertyType.TO_COLOR,
        PropertyType.VIA_COLOR,
      ].includes(propertyType)
    ) {
      // For gradient colors, we need to preserve the gradient direction class
      const gradientDirectionClass = elementClasses.find((cls) =>
        cls.startsWith("bg-gradient-")
      );

      newClasses = calculateNewClasses({
        option,
        propertyType,
        oldClasses: elementClasses,
        tailwindClassesMap,
        variant,
      });

      // Ensure the gradient direction class is preserved if it existed
      if (
        gradientDirectionClass &&
        !newClasses.includes(gradientDirectionClass)
      ) {
        newClasses.push(gradientDirectionClass);
      }
    } else {
      newClasses = calculateNewClasses({
        option,
        propertyType,
        oldClasses: elementClasses,
        tailwindClassesMap,
        variant,
      });
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      oldClasses: elementClasses,
      newClasses: newClasses,
      involvedClasses: [
        {
          ...option,
          variant,
        },
      ],
      isInstantChangeOnly: true,
    });

    const propertyValue = safeExtractValueFromCss(option);

    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(propertyValue);
    }

    useDesignCommandManager.getState().sendInstantChange(updateClassesChange);
  },
  25,
  {
    leading: true,
    trailing: false,
  }
);

export const TailwindStylingPanel = ({
  parentCssEvals,
  selectedTreeElement,
  cssValues,
}: Props) => {
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const multiselectedElements: TempoElement[] = useSelector(
    selectMultiSelectedTempoElements
  );

  let errorText: string | undefined;
  if (
    !selectedElement ||
    selectedElement.isEmpty() ||
    selectedElement.isStoryboard()
  ) {
    errorText = "Select an element to edit styles";
  } else if (!selectedElement.codebaseId) {
    errorText = "Can't find element in codebase, please select another element";
  } else if (multiselectedElements?.length) {
    errorText = "Can't edit styling for multiple elements at once";
  }

  if (errorText) {
    return (
      <div className="px-4 py-2 flex flex-row justify-center items-center">
        <div className="absolute top-1/3 flex flex-col justify-center border-dashed border border-secondary-600 p-4 m-2">
          <div className="flex flex-row text-secondary-900 items-center justify-center space-x-2">
            <CursorArrowIcon className="size-3 shrink-0" />
            <div className="text-secondary-900 text-2xs font-medium">
              {errorText}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TailwindStylingPanelContents
      parentCssEvals={parentCssEvals}
      selectedTreeElement={selectedTreeElement}
      cssValues={cssValues}
    />
  );
};

const TailwindStylingPanelContents = ({
  parentCssEvals,
  selectedTreeElement,
  cssValues,
}: Props) => {
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);

  const containerRef = useRef<HTMLDivElement>(null);
  const borderModalRef = useRef<HTMLDivElement>(null);
  const fillModalRef = useRef<HTMLDivElement>(null);

  const { classes: elementClasses } =
    useEditingClassesByCodebaseId(selectedTreeElement);

  const [manualEdittingClasses, setManualEdittingClasses] = useState<string>(
    elementClasses.join(" ")
  );

  useEffect(() => {
    setManualEdittingClasses(elementClasses.join(" "));
  }, [elementClasses]);

  const [modal, setModal] = useState<ModalState>({
    type: StylingModalType.None,
  });

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap
  );
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  const textColorModalRef = useRef<HTMLDivElement>(null);

  const buildFontWeightColorPreview = (
    option: ComboBoxOptionType
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className="w-full h-full" style={styles}>
        Tt
      </div>
    );
  };

  const buildTextColorPreview = (option: ComboBoxOptionType): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className="w-full h-full font-bold" style={styles}>
        Aa
      </div>
    );
  };

  const lineHeightProperties = useTailwindProperties(PropertyType.LINE_HEIGHT);
  const lineHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LINE_HEIGHT,
    cssValues,
  });

  const letterSpacingProperties = useTailwindProperties(
    PropertyType.LETTER_SPACING
  );
  const letterSpacingState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LETTER_SPACING,
    cssValues,
  });

  const textColorProperties = useTailwindProperties(PropertyType.TEXT_COLOR);
  const textColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_COLOR,
    cssValues,
  });
  const textColorDisplayHex = useMemo(() => {
    if (validHex(textColorState.displayValue)) {
      return textColorState.displayValue;
    }
    if (validHex(textColorState.propertyValue)) {
      return textColorState.propertyValue;
    }
    return textColorState.cssValue;
  }, [textColorState]);

  const fontSizeProperties = useTailwindProperties(PropertyType.FONT_SIZE);
  const fontSizeState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_SIZE,
    cssValues,
  });

  const widthProperties = useTailwindProperties(PropertyType.WIDTH);
  const widthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.WIDTH,
    cssValues,
  });

  const minWidthProperties = useTailwindProperties(PropertyType.MIN_WIDTH);
  const minWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MIN_WIDTH,
    cssValues,
  });

  const maxWidthProperties = useTailwindProperties(PropertyType.MAX_WIDTH);
  const maxWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MAX_WIDTH,
    cssValues,
  });

  const heightProperties = useTailwindProperties(PropertyType.HEIGHT);
  const heightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.HEIGHT,
    cssValues,
  });

  const minHeightProperties = useTailwindProperties(PropertyType.MIN_HEIGHT);
  const minHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MIN_HEIGHT,
    cssValues,
  });

  const maxHeightProperties = useTailwindProperties(PropertyType.MAX_HEIGHT);
  const maxHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MAX_HEIGHT,
    cssValues,
  });

  const paddingHorizontalProperties = useTailwindProperties(
    PropertyType.PADDING_HORIZONTAL
  );
  const paddingHorizontalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_HORIZONTAL,
    cssValues,
  });

  const paddingVerticalProperties = useTailwindProperties(
    PropertyType.PADDING_VERTICAL
  );
  const paddingVerticalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_VERTICAL,
    cssValues,
  });

  const paddingLeftProperties = useTailwindProperties(
    PropertyType.PADDING_LEFT
  );
  const paddingLeftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_LEFT,
    cssValues,
  });

  const paddingRightProperties = useTailwindProperties(
    PropertyType.PADDING_RIGHT
  );
  const paddingRightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_RIGHT,
    cssValues,
  });

  const paddingTopProperties = useTailwindProperties(PropertyType.PADDING_TOP);
  const paddingTopState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_TOP,
    cssValues,
  });

  const paddingBottomProperties = useTailwindProperties(
    PropertyType.PADDING_BOTTOM
  );
  const paddingBottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_BOTTOM,
    cssValues,
  });

  const marginHorizontalProperties = useTailwindProperties(
    PropertyType.MARGIN_HORIZONTAL
  );
  const marginHorizontalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_HORIZONTAL,
    cssValues,
  });

  const marginVerticalProperties = useTailwindProperties(
    PropertyType.MARGIN_VERTICAL
  );
  const marginVerticalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_VERTICAL,
    cssValues,
  });

  const marginBottomProperties = useTailwindProperties(
    PropertyType.MARGIN_BOTTOM
  );
  const marginBottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_BOTTOM,
    cssValues,
  });

  const marginTopProperties = useTailwindProperties(PropertyType.MARGIN_TOP);
  const marginTopState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_TOP,
    cssValues,
  });

  const marginLeftProperties = useTailwindProperties(PropertyType.MARGIN_LEFT);
  const marginLeftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_LEFT,
    cssValues,
  });

  const marginRightProperties = useTailwindProperties(
    PropertyType.MARGIN_RIGHT
  );
  const marginRightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_RIGHT,
    cssValues,
  });

  const positionProperties = useTailwindProperties(PropertyType.POSITION);
  const positionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.POSITION,
    cssValues,
  });

  const topProperties = useTailwindProperties(PropertyType.TOP);
  const topState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TOP,
    cssValues,
  });

  const leftProperties = useTailwindProperties(PropertyType.LEFT);
  const leftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LEFT,
    cssValues,
  });

  const rightProperties = useTailwindProperties(PropertyType.RIGHT);
  const rightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.RIGHT,
    cssValues,
  });

  const bottomProperties = useTailwindProperties(PropertyType.BOTTOM);
  const bottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BOTTOM,
    cssValues,
  });

  const fontWeightProperties = useTailwindProperties(PropertyType.FONT_WEIGHT);
  const fontWeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_WEIGHT,
    cssValues,
  });

  const textAlignState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_ALIGN,
    cssValues,
  });

  const fontFamilyProperties = useTailwindProperties(PropertyType.FONT_FAMILY);
  const fontFamilyState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_FAMILY,
    cssValues,
  });

  const textDecorationLineProperties = useTailwindProperties(
    PropertyType.TEXT_DECORATION_LINE
  );
  const textDecorationLineState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_DECORATION_LINE,
    cssValues,
  });

  const fontStyleProperties = useTailwindProperties(PropertyType.FONT_STYLE);
  const fontStyleState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_STYLE,
    cssValues,
  });

  const displayProperties = useTailwindProperties(PropertyType.DISPLAY);
  const displayState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.DISPLAY,
    cssValues,
  });

  const flexDirectionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_DIRECTION,
    cssValues,
  });

  const justifyContentState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.JUSTIFY_CONTENT,
    cssValues,
  });

  const alignItemsState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ALIGN_ITEMS,
    cssValues,
  });

  const columnGapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.COLUMN_GAP,
    cssValues,
  });

  const rowGapProperties = useTailwindProperties(PropertyType.ROW_GAP);
  const rowGapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ROW_GAP,
    cssValues,
  });

  const flexWrapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_WRAP,
    cssValues,
  });

  const alignContentState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ALIGN_CONTENT,
    cssValues,
  });

  const opacityProperties = useTailwindProperties(PropertyType.OPACITY);
  const opacityState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.OPACITY,
    cssValues,
  });

  const borderRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_RADIUS
  );
  const borderRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_RADIUS,
    cssValues,
  });
  const topLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_LEFT_RADIUS
  );
  const topLeftRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_LEFT_RADIUS,
    cssValues,
  });

  const topRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_RIGHT_RADIUS
  );
  const topRightRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_RIGHT_RADIUS,
    cssValues,
  });

  const bottomRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_RIGHT_RADIUS
  );
  const bottomRightRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
    cssValues,
  });

  const bottomLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_LEFT_RADIUS
  );
  const bottomLeftRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
    cssValues,
  });
  const rotationProperties = useTailwindProperties(PropertyType.ROTATE);
  const rotationState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ROTATE,
    cssValues,
  });

  const borderColorProperties = useTailwindProperties(
    PropertyType.BORDER_COLOR
  );
  const borderColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_COLOR,
    cssValues,
  });

  const borderStyleProperties = useTailwindProperties(
    PropertyType.BORDER_STYLE
  );
  const borderStyleState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_STYLE,
    cssValues,
  });

  const borderWidthProperties = useTailwindProperties(
    PropertyType.BORDER_WIDTH
  );
  const borderWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_WIDTH,
    cssValues,
  });

  const borderLeftWidthProperties = useTailwindProperties(
    PropertyType.BORDER_LEFT_WIDTH
  );
  const borderLeftWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_LEFT_WIDTH,
    cssValues,
  });

  const borderRightWidthProperties = useTailwindProperties(
    PropertyType.BORDER_RIGHT_WIDTH
  );
  const borderRightWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_RIGHT_WIDTH,
    cssValues,
  });
  const borderTopWidthProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_WIDTH
  );
  const borderTopWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_WIDTH,
    cssValues,
  });

  const borderBottomWidthProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_WIDTH
  );
  const borderBottomWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
    cssValues,
  });

  const backgroundColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_COLOR,
    cssValues,
  });

  const backgroundImageState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_IMAGE,
    cssValues,
  });

  const animationProperties = useTailwindProperties(PropertyType.ANIMATION);
  const animationState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ANIMATION,
    cssValues,
  });

  const boxShadowProperties = useTailwindProperties(PropertyType.BOX_SHADOW);
  const boxShadowState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BOX_SHADOW,
    cssValues,
  });

  const blurProperties = useTailwindProperties(PropertyType.BLUR);
  const blurState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BLUR,
    cssValues,
  });

  const flexGrowProperties = useTailwindProperties(PropertyType.FLEX_GROW);
  const flexGrowState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_GROW,
    cssValues,
  });

  const flexShrinkProperties = useTailwindProperties(PropertyType.FLEX_SHRINK);
  const flexShrinkState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_SHRINK,
    cssValues,
  });

  const [showSizingSection, setShowSizingSection] = useState(false);
  const [userOpenedSizingSection, setUserOpenedSizingSection] = useState(false);
  const [showMinWidth, setShowMinWidth] = useState(false);
  const [showMaxWidth, setShowMaxWidth] = useState(false);
  const [showMinHeight, setShowMinHeight] = useState(false);
  const [showMaxHeight, setShowMaxHeight] = useState(false);
  const [showSizingSectionDropdown, setShowSizingSectionDropdown] =
    useState(false);
  const [showSpacingSection, setShowSpacingSection] = useState(false);
  const [paddingExpanded, setPaddingExpanded] = useState(false);
  const [paddingExpandEnabled, setPaddingExpandEnabled] = useState(true);
  const [marginExpanded, setMarginExpanded] = useState(false);
  const [marginExpandEnabled, setMarginExpandEnabled] = useState(true);
  const [showPositionSection, setShowPositionSection] = useState(false);
  const [showTextSection, setShowTextSection] = useState(false);
  const [showStyleSection, setShowStyleSection] = useState(false);
  const [radiusExpanded, setRadiusExpanded] = useState(false);
  const [activeRadius, setActiveRadius] = useState(0);
  const [borderWidthExpanded, setBorderWidthExpanded] = useState(false);
  const [borderSet, setBorderSet] = useState(false);
  const [fillSet, setFillSet] = useState(false);
  const [showEffectSection, setShowEffectSection] = useState(false);

  const growOrShrinkSet = flexGrowState.isSet || flexShrinkState.isSet;
  const showFlexGrowShrinkSection = growOrShrinkSet;
  const parentFlexDirection = parentCssEvals?.["flex-direction"] || "row";
  const [rotationMode, setRotationMode] = useState("");

  const [flexGrowShrink, setFlexGrowShrink] = useState("");

  // For adding classes panel
  const [addClassesOpen, setAddClassessOpen] = useState(false);
  const [addClassOpen, setAddClassOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const startEditingManualClasses = () => {
    setIsFocused(true);
  };

  const stopEditingManualClasses = () => {
    setIsFocused(false);
  };

  const listRef = useRef<List | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentWord, setCurrentWord] = useState("");

  const handleSearch = (value: string) => {
    if (searchTerm === value) {
      return;
    }

    setSearchTerm(value);
    const parts = value.split(" ");
    setCurrentWord(parts[parts.length - 1]);
    if (listRef.current) {
      listRef.current.scrollTo(0);
    }
  };

  const filteredProperties = useMemo(() => {
    const properties: EnhancedClassType[] = (tailwindClasses || [])
      .map((cls) => {
        let score = 0;

        if (!currentWord) {
          score = 1; // So that all items are considered when there's no currentWord
        } else {
          const term = currentWord.toLowerCase();

          if (cls.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (cls.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (cls.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (cls.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...cls, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    return properties;
  }, [currentWord, addClassOpen, tailwindClasses]);

  const handleFlexGrowShrinkDropdownSelected = (option: DropdownOptionType) => {
    const value: FlexGrowShrinkOptions = option.value;

    setFlexGrowShrink(option.value);

    const oldClasses = elementClasses;
    let newClasses = [...oldClasses];
    const involvedClasses: ClassTypeWithPropertyType[] = [];

    switch (value) {
      case FlexGrowShrinkOptions.Grow:
        newClasses = newClasses.filter((cls) => cls !== "shrink");
        newClasses.push("grow");
        involvedClasses.push({
          tailwind: "grow",
          css: "flex-grow: 1",
          propertyType: PropertyType.FLEX_GROW,
        });
        break;
      case FlexGrowShrinkOptions.Shrink:
        newClasses = newClasses.filter((cls) => cls !== "grow");
        newClasses.push("shrink");
        involvedClasses.push({
          tailwind: "shrink",
          css: "flex-shrink: 1",
          propertyType: PropertyType.FLEX_SHRINK,
        });
        break;
      case FlexGrowShrinkOptions.NoGrowShrink:
        newClasses = newClasses.filter(
          (cls) => cls !== "grow" && cls !== "shrink"
        );
        break;
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: involvedClasses,
      isInstantChangeOnly: false,
    });

    executeCommand(updateClassesChange);
  };

  type EffectOption = {
    properties: ClassType[];
    propertyType: PropertyType;
    type: string;
    exclusive: boolean;
    icon: ReactNode;
  };

  type EffectClass = {
    value: string;
  };

  type Effect = EffectOption & EffectClass;

  const effectOptions: EffectOption[] = [
    {
      properties: useTailwindProperties(PropertyType.ANIMATION),
      propertyType: PropertyType.ANIMATION,
      type: "Animation",
      exclusive: true,
      icon: <MagicWandIcon />,
    },
    {
      properties: useTailwindProperties(PropertyType.BOX_SHADOW),
      propertyType: PropertyType.BOX_SHADOW,
      type: "Shadow",
      exclusive: true,
      icon: <ShadowIcon />,
    },
    {
      properties: useTailwindProperties(PropertyType.BLUR),
      propertyType: PropertyType.BLUR,
      type: "Blur",
      exclusive: true,
      icon: <BlendingModeIcon />,
    },
  ];

  useEffect(() => {
    setShowEffectSection(
      animationState.isSet || boxShadowState.isSet || blurState.isSet
    );
  }, [
    animationState.isSet,
    boxShadowState.isSet,
    blurState.isSet,
    selectedElement,
  ]);

  useEffect(() => {
    setFillSet(
      backgroundColorState.isSet ||
        backgroundImageState.isSet ||
        (typeof cssValues["background-image"]?.evaluatedValue === "string" &&
          cssValues["background-image"].evaluatedValue.includes("url"))
    );
  }, [
    backgroundImageState.isSet,
    backgroundColorState.isSet,
    cssValues["background-image"]?.evaluatedValue,
    selectedElement,
  ]);

  useEffect(() => {
    setBorderSet(
      borderColorState.isSet || borderWidthState.isSet || borderStyleState.isSet
    );
  }, [
    borderColorState.isSet,
    borderWidthState.isSet,
    borderStyleState.isSet,
    selectedElement,
  ]);

  const isFlex =
    displayState.propertyValue === "flex" ||
    cssValues?.display?.evaluatedValue === "flex";

  useEffect(() => {
    // Check if there's a background image or gradient in the CSS values
    const backgroundImageValue = cssValues["background-image"];
    const hasBackgroundImageOrGradient =
      typeof backgroundImageValue?.evaluatedValue === "string" &&
      (backgroundImageValue.evaluatedValue.includes("url") ||
        backgroundImageValue.evaluatedValue.includes("gradient"));

    setShowStyleSection(
      opacityState.isSet ||
        backgroundColorState.isSet ||
        backgroundImageState.isSet ||
        hasBackgroundImageOrGradient ||
        borderRadiusState.isSet ||
        rotationState.isSet ||
        borderSet
    );
  }, [
    selectedElement,
    opacityState.isSet,
    backgroundColorState.isSet,
    backgroundImageState.isSet,
    cssValues?.["background-image"]?.evaluatedValue,
    borderRadiusState.isSet,
    rotationState.isSet,
    borderSet,
  ]);

  useEffect(() => {
    const textPropertiesSet =
      fontWeightState.isSet ||
      lineHeightState.isSet ||
      fontSizeState.isSet ||
      letterSpacingState.isSet ||
      textColorState.isSet ||
      textAlignState.isSet;
    setShowTextSection(textPropertiesSet);
  }, [
    selectedElement,
    fontWeightState.isSet,
    lineHeightState.isSet,
    fontSizeState.isSet,
    letterSpacingState.isSet,
    textColorState.isSet,
    textAlignState.isSet,
  ]);

  useEffect(() => {
    if (
      marginBottomState.propertyValue !== marginTopState.propertyValue ||
      marginLeftState.propertyValue !== marginRightState.propertyValue
    ) {
      setMarginExpanded(true);
      setMarginExpandEnabled(false);
    } else {
      setMarginExpanded(false);
      setMarginExpandEnabled(true);
    }
  }, [
    marginBottomState.propertyValue,
    marginTopState.propertyValue,
    marginLeftState.propertyValue,
    marginRightState.propertyValue,
  ]);

  useEffect(() => {
    setShowPositionSection(
      leftState.isSet ||
        topState.isSet ||
        rightState.isSet ||
        bottomState.isSet ||
        positionState.isSet
    );
  }, [
    topState.isSet,
    leftState.isSet,
    rightState.isSet,
    bottomState.isSet,
    positionState.isSet,
    selectedElement,
  ]);

  useEffect(() => {
    setShowSpacingSection(
      paddingBottomState.isSet ||
        paddingTopState.isSet ||
        paddingLeftState.isSet ||
        paddingRightState.isSet ||
        marginTopState.isSet ||
        marginBottomState.isSet ||
        marginLeftState.isSet ||
        marginRightState.isSet ||
        paddingHorizontalState.isSet ||
        paddingVerticalState.isSet
    );
  }, [
    paddingBottomState.isSet,
    paddingTopState.isSet,
    paddingLeftState.isSet,
    paddingRightState.isSet,
    marginTopState.isSet,
    marginBottomState.isSet,
    marginLeftState.isSet,
    marginRightState.isSet,
    paddingHorizontalState.isSet,
    paddingVerticalState.isSet,
    selectedElement,
  ]);

  useEffect(() => {
    if (
      widthState.isSet ||
      heightState.isSet ||
      minWidthState.isSet ||
      maxWidthState.isSet ||
      minHeightState.isSet ||
      maxHeightState.isSet
    ) {
      setShowSizingSection(true);
    } else if (!userOpenedSizingSection) {
      setShowSizingSection(false);
    }
    setShowMinWidth(minWidthState.isSet);
    setShowMaxWidth(maxWidthState.isSet);
    setShowMinHeight(minHeightState.isSet);
    setShowMaxHeight(maxHeightState.isSet);
  }, [
    widthState.isSet,
    heightState.isSet,
    minWidthState.isSet,
    maxWidthState.isSet,
    minHeightState.isSet,
    maxHeightState.isSet,
    selectedTreeElement,
    userOpenedSizingSection,
  ]);

  useEffect(() => {
    if (
      paddingBottomState.propertyValue !== paddingTopState.propertyValue ||
      paddingLeftState.propertyValue !== paddingRightState.propertyValue
    ) {
      setPaddingExpanded(true);
      setPaddingExpandEnabled(false);
    } else {
      setPaddingExpanded(false);
      setPaddingExpandEnabled(true);
    }
  }, [
    paddingBottomState.propertyValue,
    paddingTopState.propertyValue,
    paddingLeftState.propertyValue,
    paddingRightState.propertyValue,
  ]);

  const handleSizingDropdownToggle = (option: DropdownOptionType) => {
    switch (option.value) {
      case "minWidth":
        if (showMinWidth) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MIN_WIDTH,
            setTemporaryDraggingValue: minWidthState.setTemporaryDraggingValue,
          });
          setShowMinWidth(false);
        } else {
          setShowMinWidth(true);
          if (minWidthProperties && minWidthProperties.length > 0) {
            pickTailwindClassForProperty({
              option: minWidthProperties[0],
              propertyType: PropertyType.MIN_WIDTH,
              setTemporaryDraggingValue:
                minWidthState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      case "maxWidth":
        if (showMaxWidth) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MAX_WIDTH,
            setTemporaryDraggingValue: maxWidthState.setTemporaryDraggingValue,
          });
          setShowMaxWidth(false);
        } else {
          setShowMaxWidth(true);
          if (maxWidthProperties && maxWidthProperties.length > 0) {
            pickTailwindClassForProperty({
              option: maxWidthProperties[0],
              propertyType: PropertyType.MAX_WIDTH,
              setTemporaryDraggingValue:
                maxWidthState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      case "minHeight":
        if (showMinHeight) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MIN_HEIGHT,
            setTemporaryDraggingValue: minHeightState.setTemporaryDraggingValue,
          });
          setShowMinHeight(false);
        } else {
          setShowMinHeight(true);
          if (minHeightProperties && minHeightProperties.length > 0) {
            pickTailwindClassForProperty({
              option: minHeightProperties[0],
              propertyType: PropertyType.MIN_HEIGHT,
              setTemporaryDraggingValue:
                minHeightState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      case "maxHeight":
        if (showMaxHeight) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MAX_HEIGHT,
            setTemporaryDraggingValue: maxHeightState.setTemporaryDraggingValue,
          });
          setShowMaxHeight(false);
        } else {
          setShowMaxHeight(true);
          if (maxHeightProperties && maxHeightProperties.length > 0) {
            pickTailwindClassForProperty({
              option: maxHeightProperties[0],
              propertyType: PropertyType.MAX_HEIGHT,
              setTemporaryDraggingValue:
                maxHeightState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      default:
        console.error("sizing option not recognized");
        break;
    }
  };

  const handleWidthDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "minWidth":
        setShowMinWidth(true);
        break;
      case "maxWidth":
        setShowMaxWidth(true);
        break;
      default:
        console.error("width option not recognized");
        break;
    }
  };

  const handleHeightDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "minHeight":
        setShowMinHeight(true);
        break;
      case "maxHeight":
        setShowMaxHeight(true);
        break;
      default:
        console.error("height option not recognized");
        break;
    }
  };

  const ALIGN_CONTENT_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: { width: number; height: number; className?: string } = {
      width: 20,
      height: 20,
    };
    return [
      {
        key: "flex-start",
        option: {
          tailwind: "content-start",
          css: "align-content: flex-start;",
        },
        labelContent: <AlignContentFlexStartIcon {...props} />,
        tooltipContent: "Align content start",
      },
      {
        key: "center",
        option: {
          tailwind: "content-center",
          css: "align-content: center;",
        },
        labelContent: <AlignContentCenterIcon {...props} />,
        tooltipContent: "Align content center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "content-end",
          css: "align-content: flex-end;",
        },
        labelContent: <AlignContentFlexEndIcon {...props} />,
        tooltipContent: "Align content end",
      },
      {
        key: "stretch",
        option: {
          tailwind: "content-stretch",
          css: "align-content: stretch;",
        },
        labelContent: (
          <AlignContentStretchIcon
            style={{
              width: 18,
              height: 18,
            }}
          />
        ),
        tooltipContent: "Align content stretch",
      },
      {
        key: "space-between",
        option: {
          tailwind: "content-between",
          css: "align-content: space-between;",
        },
        labelContent: (
          <AlignContentSpaceBetweenIcon
            style={{
              width: 18,
              height: 18,
            }}
          />
        ),
        tooltipContent: "Align content space between",
      },
      {
        key: "space-around",
        option: {
          tailwind: "content-around",
          css: "align-content: space-around;",
        },
        labelContent: <AlignContentSpaceAroundIcon {...props} />,
        tooltipContent: "Align content space around",
      },
    ];
  }, []);

  const ALIGN_ITEM_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: {
      width: number;
      height: number;
      className?: string;
      fill?: string;
    } = {
      width: 18,
      height: 18,
      fill: "currentColor",
    };

    if (flexDirectionState.propertyValue?.startsWith("row")) {
      props = {
        ...props,
        className: "rotate-90",
      };
    }

    return [
      {
        key: "flex-start",
        option: {
          tailwind: "items-start",
          css: "align-items: flex-start;",
        },
        labelContent: <AlignItemsStartIcon {...props} />,
        tooltipContent: "Align items start",
      },
      {
        key: "center",
        option: {
          tailwind: "items-center",
          css: "align-items: center;",
        },
        labelContent: <AlignItemsCenterIcon {...props} />,
        tooltipContent: "Align items center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "items-end",
          css: "align-items: flex-end;",
        },
        labelContent: <AlignItemsEndIcon {...props} />,
        tooltipContent: "Align items end",
      },
      {
        key: "stretch",
        option: {
          tailwind: "items-stretch",
          css: "align-items: stretch;",
        },
        labelContent: <AlignItemsStretchIcon {...props} />,
        tooltipContent: "Align items stretch",
      },
      {
        key: "baseline",
        option: {
          tailwind: "items-baseline",
          css: "align-items: baseline;",
        },
        labelContent: <AlignItemsBaseline {...props} />,
        tooltipContent: "Align items baseline",
      },
    ];
  }, [flexDirectionState.propertyValue]);

  const JUSTIFY_CONTENT_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: { width: number; height: number; className?: string } = {
      width: 18,
      height: 18,
    };

    if (flexDirectionState.propertyValue?.startsWith("column")) {
      props = {
        ...props,
        className: "rotate-90",
      };
    }
    return [
      {
        key: "flex-start",
        option: {
          tailwind: "justify-start",
          css: "justify-content: flex-start;",
        },
        labelContent: <JustifyFlexStartIcon {...props} />,
        tooltipContent: "Justify content start",
      },
      {
        key: "center",
        option: {
          tailwind: "justify-center",
          css: "justify-content: center;",
        },
        labelContent: <JustifyCenterIcon {...props} />,
        tooltipContent: "Justify content center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "justify-end",
          css: "justify-content: flex-end;",
        },
        labelContent: <JustifyFlexEndIcon {...props} />,
        tooltipContent: "Justify content end",
      },
      {
        key: "space-between",
        option: {
          tailwind: "justify-between",
          css: "justify-content: space-between;",
        },
        labelContent: <JustifySpaceBetweenIcon {...props} />,
        tooltipContent: "Justify content between",
      },
      {
        key: "space-evenly",
        option: {
          tailwind: "justify-evenly",
          css: "justify-content: space-evenly;",
        },
        labelContent: <JustifySpaceEvenlyIcon {...props} />,
        tooltipContent: "Justify content evenly",
      },
    ];
  }, [flexDirectionState.propertyValue]);

  const TEXT_ALIGN_OPTIONS: DiscreteOption[] = [
    {
      key: "left",
      option: {
        tailwind: "text-left",
        css: "text-align: left",
      },
      labelContent: <TextAlignLeftIcon />,
      tooltipContent: "Text align left",
    },
    {
      key: "center",
      option: {
        tailwind: "text-center",
        css: "text-align: center",
      },
      labelContent: <TextAlignCenterIcon />,
      tooltipContent: "Text align center",
    },
    {
      key: "right",
      option: {
        tailwind: "text-right",
        css: "text-align: right",
      },
      labelContent: <TextAlignRightIcon />,
      tooltipContent: "Text align right",
    },
    {
      key: "justify",
      option: {
        tailwind: "text-justify",
        css: "text-align: justify",
      },
      labelContent: <TextAlignJustifyIcon />,
      tooltipContent: "Text align justified",
    },
  ];

  const TEXT_DECORATION_LINE_OPTIONS: DiscreteOption[] = [
    {
      key: "underline",
      option: {
        tailwind: "underline",
        css: "text-decoration-line: underline",
      },
      labelContent: <UnderlineIcon />,
      tooltipContent: "Underline",
    },
    {
      key: "line-through",
      option: {
        tailwind: "line-through",
        css: "text-decoration-line: line-through",
      },
      labelContent: <StrikethroughIcon />,
      tooltipContent: "Line Through",
    },
    {
      key: "overline",
      option: {
        tailwind: "overline",
        css: "text-decoration-line: overline",
      },
      labelContent: <OverlineIcon />,
      tooltipContent: "Overline",
    },
  ];

  const executeCommand = useDesignCommandManager(
    (state) => state.executeCommand
  );

  const clearTailwindClassForProperty = ({
    propertyType,
    setTemporaryDraggingValue,
    variant,
  }: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => {
    const newClasses = calculateNewClasses({
      option: null,
      propertyType,
      oldClasses: elementClasses,
      tailwindClassesMap,
      variant,
    });
    if (
      newClasses.length === elementClasses.length &&
      newClasses.every((cls, i) => cls === elementClasses[i])
    ) {
      if (setTemporaryDraggingValue) {
        setTemporaryDraggingValue(null);
      }
      return;
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: [],
      isInstantChangeOnly: false,
    });
    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }
    executeCommand(updateClassesChange);
  };

  const clearTailwindClassForMultipleProperties = ({
    propertyTypes,
    setTemporaryDraggingValue,
    variant,
    thenSetOption,
    thenSetPropertyType,
  }: {
    propertyTypes: PropertyType[];
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    thenSetOption?: ComboBoxOptionType;
    thenSetPropertyType?: PropertyType;
  }) => {
    let newClasses = [...elementClasses];

    // Apply each property type removal sequentially
    for (const propertyType of propertyTypes) {
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant,
      });
    }

    // If we have an option to set after clearing, apply it
    if (thenSetOption && thenSetPropertyType) {
      newClasses = calculateNewClasses({
        option: thenSetOption,
        propertyType: thenSetPropertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant,
      });
    }

    // Check if any changes were made
    if (
      newClasses.length === elementClasses.length &&
      newClasses.every((cls, i) => cls === elementClasses[i])
    ) {
      if (setTemporaryDraggingValue) {
        setTemporaryDraggingValue(null);
      }
      return;
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: thenSetOption ? [thenSetOption] : [],
      isInstantChangeOnly: false,
    });

    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }

    executeCommand(updateClassesChange);
  };

  const pickTailwindClassForProperty = ({
    option,
    propertyType,
    setTemporaryDraggingValue,
    variant,
  }: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => {
    const newClasses = calculateNewClasses({
      option,
      propertyType,
      oldClasses: elementClasses,
      tailwindClassesMap,
      variant,
    });

    if (
      newClasses.length === elementClasses.length &&
      newClasses.every((cls, i) => cls === elementClasses[i])
    ) {
      if (setTemporaryDraggingValue) {
        setTemporaryDraggingValue(null);
      }
      return;
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: [
        {
          ...option,
          variant,
        },
      ],
      isInstantChangeOnly: false,
    });
    executeCommand(updateClassesChange);
    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }
  };

  const elementClassesRef = useRef(elementClasses);
  useEffect(() => {
    elementClassesRef.current = elementClasses;
  }, [elementClasses]);

  const tailwindClassesMapRef = useRef(tailwindClassesMap);
  useEffect(() => {
    tailwindClassesMapRef.current = tailwindClassesMap;
  }, [tailwindClassesMap]);

  const selectedElementRef = useRef(selectedElement);
  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  const updateInstantForProperty = useCallback(
    ({
      propertyType,
      option,
      setTemporaryDraggingValue,
      variant,
    }: {
      propertyType: PropertyType;
      option: ComboBoxOptionType;
      setTemporaryDraggingValue: (value: string | null) => void;
      variant?: string | null;
    }) => {
      updateInstantForPropertyThrottled({
        propertyType,
        option,
        setTemporaryDraggingValue,
        variant,
        elementClasses: elementClassesRef.current || [],
        tailwindClassesMap: tailwindClassesMapRef.current || {},
        selectedElement: selectedElementRef.current || null,
      });
    },
    []
  );

  const applyNewClasses = (value?: string) => {
    const newClasses = (value ?? manualEdittingClasses)
      .split(" ")
      .filter(Boolean);
    const oldClasses = elementClasses;
    if (
      newClasses.length === oldClasses.length &&
      newClasses.every((cls, i) => cls === oldClasses[i])
    ) {
      setManualEdittingClasses(oldClasses.join(" "));
      return;
    }
    const missingClasses = newClasses.filter(
      (cls) => !oldClasses.includes(cls)
    );
    const involvedClasses: {
      tailwind: string;
      css: string;
      variant?: string | null;
    }[] = [];
    for (const cls of missingClasses) {
      if (isClassVariant(cls)) {
        const [variant, actualClassName] = cls.split(":");
        if (tailwindClassesMap[actualClassName]) {
          involvedClasses.push({
            ...tailwindClassesMap[actualClassName],
            variant,
          });
        }
      } else if (tailwindClassesMap[cls]) {
        involvedClasses.push(tailwindClassesMap[cls]);
      }
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      oldClasses: elementClasses,
      newClasses: newClasses,
      involvedClasses: involvedClasses,
      isInstantChangeOnly: false,
    });
    executeCommand(updateClassesChange);
    setManualEdittingClasses(newClasses.join(" "));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyNewClasses();
    }
  };

  // For text color variant modal
  const [textColorVariantModalOpen, setTextColorVariantModalOpen] =
    useState(false);
  const textColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add state and ref for border color variant modal
  const [borderColorVariantModalOpen, setBorderColorVariantModalOpen] =
    useState(false);
  const borderColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // 1. Add state and ref for fill variant modal (near other variant modal states)
  const [fillColorVariantModalOpen, setFillColorVariantModalOpen] =
    useState(false);
  const fillColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add this with other property arrays at the top
  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR
  );

  // Add with other refs
  const fillModalPopoverRef = useRef<HTMLDivElement>(null);

  // Add state and ref for gradient color stop variant modals/anchors
  const [fromColorVariantModalOpen, setFromColorVariantModalOpen] =
    useState(false);
  const fromColorVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [viaColorVariantModalOpen, setViaColorVariantModalOpen] =
    useState(false);
  const viaColorVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [toColorVariantModalOpen, setToColorVariantModalOpen] = useState(false);
  const toColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add these with other property arrays and states at the top
  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);
  const fromColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FROM_COLOR,
    cssValues,
  });
  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const viaColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_COLOR,
    cssValues,
  });
  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const toColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_COLOR,
    cssValues,
  });

  const widthDropdownOptions: DropdownOptionType[] = [
    {
      label: "Add min width...",
      value: "minWidth",
      icon: <MinWidthIcon />,
    },
    {
      label: "Add max width...",
      value: "maxWidth",
      icon: <MaxWidthIcon />,
    },
  ];
  const heightDropdownOptions: DropdownOptionType[] = [
    {
      label: "Add min height...",
      value: "minHeight",
      icon: <MinWidthIcon className="rotate-90" />,
    },
    {
      label: "Add max height...",
      value: "maxHeight",
      icon: <MaxWidthIcon className="rotate-90" />,
    },
  ];

  const createIcon = (Icon, rotate = false) => (
    <Icon className={rotate ? "rotate-90" : ""} />
  );

  const getLabel = (value, active) => {
    const prefix = value.replace(/([A-Z])/g, " $1").toLowerCase();
    return `${active ? "Remove" : "Add"} ${prefix}...`;
  };

  const sizingConfig = [
    { value: "minWidth", Icon: MinWidthIcon, show: showMinWidth },
    { value: "maxWidth", Icon: MaxWidthIcon, show: showMaxWidth },
    {
      value: "minHeight",
      Icon: MinWidthIcon,
      show: showMinHeight,
      rotate: true,
    },
    {
      value: "maxHeight",
      Icon: MaxWidthIcon,
      show: showMaxHeight,
      rotate: true,
    },
  ];

  const sizingDropdownOptions = sizingConfig.map(
    ({ value, Icon, show, rotate }) => ({
      label: getLabel(value, show),
      value,
      icon: createIcon(Icon, rotate),
    })
  );
  const toggleArbitraryClass = (className: string, css: string) => {
    const oldClasses = elementClasses;
    let newClasses = [...oldClasses];
    const idx = newClasses.indexOf(className);
    if (idx !== -1) {
      newClasses.splice(idx, 1);
    } else {
      newClasses.push(className);
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      oldClasses: oldClasses,
      newClasses: newClasses,
      involvedClasses: [
        {
          tailwind: className,
          css: css,
        },
      ],
      isInstantChangeOnly: false,
    });
    executeCommand(updateClassesChange);
  };

  const resetRotationAndFlips = () => {
    const oldClasses = elementClasses;
    const newClasses = oldClasses.filter(
      (cls) =>
        !cls.startsWith("rotate-") &&
        cls !== "scale-x-[-1]" &&
        cls !== "scale-y-[-1]"
    );
    if (newClasses.length === oldClasses.length) return;
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
      oldClasses: oldClasses,
      newClasses: newClasses,
      involvedClasses: [],
      isInstantChangeOnly: false,
    });
    executeCommand(updateClassesChange);
  };

  const showPropManager = useMemo(() => {
    return selectedTreeElement?.type === "component-instance";
  }, [selectedTreeElement]);

  return (
    <div className="text-xs relative" ref={containerRef}>
      <ComboboxContainer>
        <div className="px-4 py-2 pb-0">
          <>
            <div className="flex flex-row justify-between items-center mb-2">
              <div
                className={cn(
                  "flex items-center gap-2 text-xs font-medium",
                  !addClassesOpen && "text-muted-foreground"
                )}
              >
                <img
                  height={16}
                  width={16}
                  src="/images/tailwind-logo.svg"
                  alt="Add a Tailwind class"
                />
                Classes
              </div>
              <div
                className={"flex flex-col items-center justify-center w-1/12"}
                onClick={() => {
                  setAddClassessOpen((prev) => !prev);
                }}
              >
                <ClickableIcon>
                  {addClassesOpen ? <MinusIcon /> : <PlusIcon />}
                </ClickableIcon>
              </div>
            </div>

            {/* Popover sizes */}
            {addClassesOpen ? (
              <Popover open={addClassOpen}>
                <PopoverTrigger asChild style={{ zIndex: 1000 }}>
                  <div
                    className={cn(
                      "relative group pb-6 mb-4 bg-secondary-n-1000 hover:bg-secondary-n-950 cursor-pointer rounded-md flex flex-row justify-between border-secondary-n-900 items-center border",
                      isFocused && "border-primary-0"
                    )}
                    data-tooltip-content="Add Tailwind class"
                    data-tooltip-id="tooltip"
                    data-tooltip-place="top"
                  >
                    <Textarea
                      onKeyDown={handleKeyDown}
                      value={
                        manualEdittingClasses === ""
                          ? elementClasses.join(" ")
                          : manualEdittingClasses
                      }
                      onFocus={startEditingManualClasses}
                      onBlur={stopEditingManualClasses}
                      placeholder="Search classes"
                      onChange={(e) => setManualEdittingClasses(e.target.value)}
                      className="mb-3 resize-none w-full min-h-[80px] text-xs text-secondary-n-50 "
                    />
                    <div className="flex border border-secondary-n-900 mt-2 items-center gap-1 absolute bg-secondary-n-950 rounded-md bottom-1 right-1 text-secondary-n-50">
                      <ClickableIcon onClick={() => setAddClassOpen(true)}>
                        <PlusIcon />
                      </ClickableIcon>
                    </div>
                    {isFocused &&
                      manualEdittingClasses.trim() !==
                        elementClasses.join(" ").trim() && (
                        <div className="flex border border-secondary-n-900 items-center gap-1 absolute bg-secondary-n-950 p-1 px-1.5 rounded-md bottom-1 right-9 text-secondary-n-50">
                          Enter to apply <ArrowUp className="w-4 h-4 " />
                        </div>
                      )}
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className="h-[400px] max-w-64 z-[1004] text-2xs p-0 rounded-xl"
                  side="bottom"
                  align="start"
                  onInteractOutside={() => setAddClassOpen(false)}
                >
                  <Command
                    shouldFilter={false}
                    loop={false}
                    className="p-0 rounded-xl"
                  >
                    <div className="pb-1 border-secondary-n-900 p-1.5">
                      <CommandInput
                        placeholder="Search for a classes"
                        onValueChange={handleSearch}
                        rootClassName="px-1 py-1 bg-secondary-n-950"
                      />
                    </div>
                    <div className="p-1.5">
                      <List
                        id="tailwind-classes-list"
                        ref={listRef}
                        height={40 * 12}
                        itemCount={filteredProperties.length}
                        itemSize={40}
                        width="100%"
                      >
                        {({ index, style }) => (
                          <Row
                            index={index}
                            style={style}
                            filteredProperties={filteredProperties}
                            handleSelect={(cls) => {
                              setManualEdittingClasses((current) => {
                                const newValue =
                                  `${current} ${cls.tailwind}`.trim();
                                if (
                                  newValue !== elementClasses.join(" ").trim()
                                ) {
                                  setTimeout(
                                    () => applyNewClasses(newValue),
                                    0
                                  );
                                }
                                return newValue;
                              });
                            }}
                          />
                        )}
                      </List>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : null}
          </>
        </div>
        <hr />
        {showPropManager && selectedTreeElement && (
          <>
            <div className="px-4 py-2 flex flex-col gap-y-2 relative">
              <PropManager />
            </div>
            <hr />
          </>
        )}
        <div className="px-4 py-2 flex flex-col gap-y-2 relative">
          <div className="flex  flex-row justify-between items-center">
            <div
              className={cn(
                "text-xs font-medium",
                !showPositionSection && "text-muted-foreground"
              )}
            >
              Position
            </div>
            <div
              className={"flex flex-col items-center justify-center w-1/12"}
              onClick={() => {
                setShowPositionSection((prev) => !prev);
              }}
            >
              <ClickableIcon>
                {showPositionSection ? <MinusIcon /> : <PlusIcon />}
              </ClickableIcon>
            </div>
          </div>
          {/* Position */}
          {showPositionSection && (
            <div className="mt-2 pb-2">
              <div className="items-center gap-x-3 gap-y-2">
                <div className="mb-2">
                  <DraggableInput
                    propertyState={positionState}
                    placeholder="Position"
                    comboboxOptions={positionProperties}
                    onComboboxSelected={(option, variant) => {
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.POSITION,
                        setTemporaryDraggingValue:
                          positionState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    onSliderValueUpdate={(option, variant) => {
                      updateInstantForProperty({
                        propertyType: PropertyType.POSITION,
                        option,
                        setTemporaryDraggingValue:
                          positionState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    draggable={false}
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.POSITION,
                        setTemporaryDraggingValue:
                          positionState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    tooltipContent="Position"
                  />
                </div>

                {positionState.propertyValue !== "static" &&
                  positionState.propertyValue !== "sticky" && (
                    <div className="grid grid-cols-2 gap-2">
                      {positionState.propertyValue !== "static" && (
                        <>
                          <DraggableInput
                            propertyState={topState}
                            labelContent={
                              <AlignItemsEndIcon className="-rotate-90" />
                            }
                            disabled={positionState.propertyValue === "static"}
                            comboboxOptions={topProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.TOP,
                                setTemporaryDraggingValue:
                                  topState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType: PropertyType.TOP,
                                option,
                                setTemporaryDraggingValue:
                                  topState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onClear={(variant?: string) => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.TOP,
                                setTemporaryDraggingValue:
                                  topState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `top-[${input}]`,
                                css: `top: ${input}`,
                              };
                            }}
                            step={1}
                            fallback={{ number: 0, unit: "px" }}
                            tooltipContent="Top"
                          />
                        </>
                      )}

                      <DraggableInput
                        propertyState={bottomState}
                        labelContent={
                          <AlignItemsStartIcon className="-rotate-90" />
                        }
                        disabled={positionState.propertyValue === "static"}
                        comboboxOptions={bottomProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.BOTTOM,
                            setTemporaryDraggingValue:
                              bottomState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.BOTTOM,
                            option,
                            setTemporaryDraggingValue:
                              bottomState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.BOTTOM,
                            setTemporaryDraggingValue:
                              bottomState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `bottom-[${input}]`,
                            css: `bottom: ${input}`,
                          };
                        }}
                        step={1}
                        fallback={{ number: 0, unit: "px" }}
                        tooltipContent="Bottom"
                      />

                      <DraggableInput
                        propertyState={leftState}
                        labelContent={<AlignItemsStartIcon />}
                        comboboxOptions={leftProperties}
                        disabled={positionState.propertyValue === "static"}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.LEFT,
                            setTemporaryDraggingValue:
                              leftState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.LEFT,
                            option,
                            setTemporaryDraggingValue:
                              leftState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.LEFT,
                            setTemporaryDraggingValue:
                              leftState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `left-[${input}]`,
                            css: `left: ${input}`,
                          };
                        }}
                        step={1}
                        fallback={{ number: 0, unit: "px" }}
                        tooltipContent="Left"
                      />

                      <DraggableInput
                        propertyState={rightState}
                        labelContent={<AlignItemsEndIcon />}
                        comboboxOptions={rightProperties}
                        disabled={positionState.propertyValue === "static"}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.RIGHT,
                            setTemporaryDraggingValue:
                              rightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.RIGHT,
                            option,
                            setTemporaryDraggingValue:
                              rightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.RIGHT,
                            setTemporaryDraggingValue:
                              rightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `right-[${input}]`,
                            css: `right: ${input}`,
                          };
                        }}
                        step={1}
                        fallback={{ number: 0, unit: "px" }}
                        tooltipContent="Right"
                      />
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2 flex flex-col gap-y-2 relative">
          <div className="flex flex-row justify-between items-center relative">
            <div
              className={cn(
                "text-xs font-medium",
                !showSizingSection && "text-muted-foreground"
              )}
            >
              Size
            </div>
            <div
              className={"flex flex-col items-center justify-center w-1/12"}
              onClick={() => {
                setShowSizingSection((prev) => {
                  const newVal = !prev;
                  if (newVal) setUserOpenedSizingSection(true);
                  return newVal;
                });
              }}
            >
              <ClickableIcon>
                {showSizingSection ? <MinusIcon /> : <PlusIcon />}
              </ClickableIcon>
            </div>
          </div>
          {showSizingSection && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="hover:bg-secondary-n-900 hover:text-secondary-n-50 p-0 h-6 w-6"
                  onClick={() => setShowSizingSectionDropdown(true)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <span className="text-xs text-secondary-n-400">
                  Add min/max width or height
                </span>
                <DropdownInput
                  className="max-w-60 mx-auto -translate-x-1/2 left-1/2"
                  visible={showSizingSectionDropdown}
                  setVisible={setShowSizingSectionDropdown}
                  dropdownOptions={sizingDropdownOptions}
                  onDropdownSelected={(option) => {
                    handleSizingDropdownToggle(option);
                    setShowSizingSectionDropdown(false);
                  }}
                />
              </div>
              {/* Flex Grow & Shrink */}
              {showFlexGrowShrinkSection &&
                (parentFlexDirection === "row" ? (
                  <div className="mt-2">
                    <div className="text-xs text-secondary-n-400 font-medium mb-2">
                      Grow
                    </div>
                    <DraggableInput
                      propertyState={{
                        propertyValue:
                          parentFlexDirection === "row" ? flexGrowShrink : "",
                        isSet: parentFlexDirection === "row",
                        displayValue:
                          parentFlexDirection === "row" ? flexGrowShrink : "",

                        // TODO: NEED TO DO
                        valueByVariant: {},
                      }}
                      labelContent={
                        horizontalFlexGrowShrinkOptions.find(
                          (option) => option.value === flexGrowShrink
                        )?.icon || horizontalFlexGrowShrinkOptions[0].icon
                      }
                      dropdownOptions={
                        parentFlexDirection === "row"
                          ? horizontalFlexGrowShrinkOptions
                          : undefined
                      }
                      onDropdownSelected={(option) => {
                        handleFlexGrowShrinkDropdownSelected(option);
                      }}
                      disabled={parentFlexDirection !== "row"}
                      draggable={false}
                      tooltipContent={"Horizontal Resizing"}
                      // TODO: Implement?
                      onClear={() => {}}
                    />
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="text-xs text-secondary-n-400 font-mediu my-1">
                      Resizing
                    </div>
                    <div>
                      <DraggableInput
                        propertyState={{
                          propertyValue:
                            parentFlexDirection === "column"
                              ? flexGrowShrink
                              : "",
                          isSet: parentFlexDirection === "column",
                          displayValue:
                            parentFlexDirection === "row" ? flexGrowShrink : "",
                          // TODO: NEED TO DO
                          valueByVariant: {},
                        }}
                        labelContent={
                          verticalFlexGrowShrinkOptions.find(
                            (option) => option.value === flexGrowShrink
                          )?.icon || verticalFlexGrowShrinkOptions[0].icon
                        }
                        dropdownOptions={
                          parentFlexDirection === "column"
                            ? verticalFlexGrowShrinkOptions
                            : undefined
                        }
                        onDropdownSelected={(option) => {
                          handleFlexGrowShrinkDropdownSelected(option);
                        }}
                        disabled={parentFlexDirection !== "column"}
                        draggable={false}
                        tooltipContent={"Vertical Resizing"}
                        // TODO: Implement?
                        onClear={() => {}}
                      />{" "}
                    </div>
                  </div>
                ))}

              {/* Width & Height */}
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-2 items-center gap-x-2 gap-y-2">
                  <DraggableInput
                    propertyState={widthState}
                    labelContent={"W"}
                    comboboxOptions={widthProperties}
                    dropdownOptions={widthDropdownOptions}
                    onDropdownSelected={(option) => {
                      handleWidthDropdownSelected(option);
                    }}
                    onComboboxSelected={(option, variant) => {
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.WIDTH,
                        setTemporaryDraggingValue:
                          widthState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    onSliderValueUpdate={(option, variant) => {
                      updateInstantForProperty({
                        propertyType: PropertyType.WIDTH,
                        option,
                        setTemporaryDraggingValue:
                          widthState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.WIDTH,
                        setTemporaryDraggingValue:
                          widthState.setTemporaryDraggingValue,
                      });
                    }}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `w-[${input}]`,
                        css: `width: ${input}`,
                      };
                    }}
                    min={0}
                    step={1}
                    tooltipContent="Width"
                  />

                  <DraggableInput
                    propertyState={heightState}
                    labelContent={"H"}
                    comboboxOptions={heightProperties}
                    dropdownOptions={heightDropdownOptions}
                    onDropdownSelected={(option) =>
                      handleHeightDropdownSelected(option)
                    }
                    onComboboxSelected={(option, variant) => {
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.HEIGHT,
                        setTemporaryDraggingValue:
                          heightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    onSliderValueUpdate={(option, variant) => {
                      updateInstantForProperty({
                        propertyType: PropertyType.HEIGHT,
                        option,
                        setTemporaryDraggingValue:
                          heightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `h-[${input}]`,
                        css: `height: ${input}`,
                      };
                    }}
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.HEIGHT,
                        setTemporaryDraggingValue:
                          heightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    min={0}
                    step={1}
                    tooltipContent="Height"
                  />
                </div>

                {/* Min/Max Width section */}

                <div className="flex flex-row gap-2">
                  <div className="flex flex-col gap-2 grow basis-0">
                    {showMinWidth && (
                      <DraggableInput
                        propertyState={minWidthState}
                        placeholder="Min W"
                        labelContent={
                          <Minimize2
                            width={15}
                            height={15}
                            className="pr-1 rotate-45"
                          />
                        }
                        comboboxOptions={minWidthProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.MIN_WIDTH,
                            setTemporaryDraggingValue:
                              minWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.MIN_WIDTH,
                            option,
                            setTemporaryDraggingValue:
                              minWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `min-w-[${input}]`,
                            css: `min-width: ${input}`,
                          };
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.MIN_WIDTH,
                            setTemporaryDraggingValue:
                              minWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        min={0}
                        step={1}
                        tooltipContent="Minimum width"
                      />
                    )}

                    {showMaxWidth && (
                      <DraggableInput
                        propertyState={maxWidthState}
                        placeholder="Max W"
                        labelContent={
                          <Maximize2
                            className="w-4 h-4 rotate-45"
                            strokeWidth={1.5}
                          />
                        }
                        comboboxOptions={maxWidthProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.MAX_WIDTH,
                            setTemporaryDraggingValue:
                              maxWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.MAX_WIDTH,
                            option,
                            setTemporaryDraggingValue:
                              maxWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.MAX_WIDTH,
                            setTemporaryDraggingValue:
                              maxWidthState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `max-w-[${input}]`,
                            css: `max-width: ${input}`,
                          };
                        }}
                        min={0}
                        step={1}
                        tooltipContent="Maximum width"
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-2 grow basis-0">
                    {showMinHeight && (
                      <DraggableInput
                        propertyState={minHeightState}
                        placeholder="Min H"
                        labelContent={
                          <Minimize2
                            className="rotate-135"
                            width={15}
                            height={15}
                          />
                        }
                        comboboxOptions={minHeightProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.MIN_HEIGHT,
                            setTemporaryDraggingValue:
                              minHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.MIN_HEIGHT,
                            option,
                            setTemporaryDraggingValue:
                              minHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.MIN_HEIGHT,
                            setTemporaryDraggingValue:
                              minHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `min-h-[${input}]`,
                            css: `min-height: ${input}`,
                          };
                        }}
                        min={0}
                        step={1}
                        tooltipContent="Minimum height"
                      />
                    )}

                    {/* Min/Max Height Section */}

                    {showMaxHeight && (
                      <DraggableInput
                        propertyState={maxHeightState}
                        placeholder="Max H"
                        labelContent={
                          <Maximize2
                            className="w-4 h-4 rotate-135"
                            strokeWidth={1.5}
                          />
                        }
                        comboboxOptions={maxHeightProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.MAX_HEIGHT,
                            setTemporaryDraggingValue:
                              maxHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.MAX_HEIGHT,
                            option,
                            setTemporaryDraggingValue:
                              maxHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.MAX_HEIGHT,
                            setTemporaryDraggingValue:
                              maxHeightState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `max-h-[${input}]`,
                            css: `max-height: ${input}`,
                          };
                        }}
                        min={0}
                        step={1}
                        tooltipContent="Maximum height"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-col">
            <div className="flex flex-row justify-between items-center">
              <div
                className={cn(
                  "text-xs font-medium",
                  !showSpacingSection && "text-muted-foreground"
                )}
              >
                Spacing
              </div>
              <div
                className={"flex flex-col items-center justify-center w-1/12"}
                onClick={() => {
                  setShowSpacingSection((prev) => !prev);
                }}
              >
                <ClickableIcon>
                  {showSpacingSection ? <MinusIcon /> : <PlusIcon />}
                </ClickableIcon>
              </div>
            </div>
            {showSpacingSection && (
              <div className="mt-4">
                <div className="text-xs text-secondary-n-400 mb-1">Padding</div>
                <div className="flex items-start justify-between gap-2">
                  {!paddingExpanded && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <DraggableInput
                          propertyState={paddingHorizontalState}
                          labelContent={<SpacingIcon />}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `px-[${input}]`,
                              css: `padding-left: ${input}; padding-right: ${input}`,
                            };
                          }}
                          comboboxOptions={paddingHorizontalProperties}
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              option,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          step={0.25}
                          min={0}
                          onComboboxSelected={(option, variant) =>
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          onClear={(variant?: string) =>
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          tooltipContent="Horizontal padding"
                        />
                      </div>
                      <div className="flex-1">
                        <DraggableInput
                          propertyState={paddingVerticalState}
                          labelContent={<SpacingIcon className="rotate-90" />}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `py-[${input}]`,
                              css: `padding-top: ${input}; padding-bottom: ${input}`,
                            };
                          }}
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.PADDING_VERTICAL,
                              option,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          step={0.25}
                          min={0}
                          comboboxOptions={paddingVerticalProperties}
                          onComboboxSelected={(option, variant) =>
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.PADDING_VERTICAL,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          onClear={(variant?: string) =>
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.PADDING_VERTICAL,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          tooltipContent="Vertical padding"
                        />
                      </div>
                    </div>
                  )}
                  {paddingExpanded && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={<AlignLeftIcon />}
                            propertyState={paddingLeftState}
                            comboboxOptions={paddingLeftProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_LEFT,
                                option,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_LEFT,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pl-[${input}]`,
                                css: `padding-left: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_LEFT,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            tooltipContent="Left padding"
                          />
                        </div>

                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="rotate-180" />
                            }
                            propertyState={paddingRightState}
                            comboboxOptions={paddingRightProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_RIGHT,
                                option,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_RIGHT,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pr-[${input}]`,
                                css: `padding-right: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_RIGHT,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            tooltipContent="Right padding"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="rotate-90" />
                            }
                            propertyState={paddingTopState}
                            comboboxOptions={paddingTopProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_TOP,
                                option,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_TOP,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pt-[${input}]`,
                                css: `padding-top: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_TOP,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            tooltipContent="Top padding"
                          />
                        </div>
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="-rotate-90" />
                            }
                            propertyState={paddingBottomState}
                            comboboxOptions={paddingBottomProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_BOTTOM,
                                option,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_BOTTOM,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pb-[${input}]`,
                                css: `padding-bottom: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_BOTTOM,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            tooltipContent="Bottom padding"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col items-end justify-center">
                    <div
                      className={
                        "flex items-center justify-center border  border-transparent  cursor-pointer p-1.5 rounded-md " +
                        `${
                          paddingExpanded
                            ? "bg-secondary-n-900 text-secondary-n-50"
                            : "bg-secondary-n-950 text-secondary-n-600"
                        }`
                      }
                      onClick={() =>
                        paddingExpandEnabled &&
                        setPaddingExpanded((prev) => !prev)
                      }
                    >
                      <ExpandSettingPadding className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-secondary-n-400 mb-1">
                    Margin
                  </div>
                </div>
                <div className="gap-x-3 gap-y-2">
                  <div className="flex items-start justify-between gap-2">
                    {!marginExpanded && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <DraggableInput
                            propertyState={marginHorizontalState}
                            labelContent={<SpacingIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `mx-[${input}]`,
                                css: `margin-left: ${input}; margin-right: ${input}`,
                              };
                            }}
                            comboboxOptions={marginHorizontalProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                option,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            tooltipContent="Horizontal margin"
                          />
                        </div>
                        <div className="flex-1">
                          <DraggableInput
                            propertyState={marginVerticalState}
                            labelContent={<SpacingIcon className="rotate-90" />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `my-[${input}]`,
                                css: `margin-top: ${input}; margin-bottom: ${input}`,
                              };
                            }}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                option,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            comboboxOptions={marginVerticalProperties}
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            tooltipContent="Vertical margin"
                          />
                        </div>
                      </div>
                    )}
                    {marginExpanded && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={<AlignLeftIcon />}
                              propertyState={marginLeftState}
                              comboboxOptions={marginLeftProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `ml-[${input}]`,
                                  css: `margin-left: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Left margin"
                            />
                          </div>
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="rotate-180" />
                              }
                              propertyState={marginRightState}
                              comboboxOptions={marginRightProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mr-[${input}]`,
                                  css: `margin-right: ${input};`,
                                };
                              }}
                              step={0.25}
                              min={0}
                              tooltipContent="Right margin"
                              onClear={(variant?: string) => {
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="rotate-90" />
                              }
                              propertyState={marginTopState}
                              comboboxOptions={marginTopProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_TOP,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_TOP,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mt-[${input}]`,
                                  css: `margin-top: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_TOP,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Top margin"
                            />
                          </div>
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="-rotate-90" />
                              }
                              propertyState={marginBottomState}
                              comboboxOptions={marginBottomProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mb-[${input}]`,
                                  css: `margin-bottom: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Bottom margin"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className=" flex flex-col items-end justify-center">
                      <div
                        className={
                          "flex items-center justify-center border  border-transparent  cursor-pointer p-1.5 rounded-md " +
                          `${
                            marginExpanded
                              ? "bg-secondary-n-900 text-secondary-n-50"
                              : "bg-secondary-n-950 text-secondary-n-600"
                          }`
                        }
                        onClick={() =>
                          marginExpandEnabled &&
                          setMarginExpanded((prev) => !prev)
                        }
                      >
                        <ExpandSettingPadding className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <hr></hr>

        <div className="px-4 py-2">
          <div className="flex flex-row justify-between items-center">
            <div
              className={cn(
                "text-xs font-medium",
                !isFlex && "text-muted-foreground"
              )}
            >
              Layout
            </div>
            <div
              className={"flex flex-col items-center justify-center w-1/12"}
              onClick={() => {
                if (!isFlex) {
                  pickTailwindClassForProperty({
                    option: {
                      tailwind: "flex",
                      css: "display: flex;",
                    },
                    propertyType: PropertyType.DISPLAY,
                    setTemporaryDraggingValue:
                      displayState.setTemporaryDraggingValue,
                  });
                } else {
                  clearTailwindClassForProperty({
                    propertyType: PropertyType.DISPLAY,
                    setTemporaryDraggingValue:
                      displayState.setTemporaryDraggingValue,
                  });
                }
              }}
            >
              <ClickableIcon>
                {!isFlex ? <PlusIcon /> : <MinusIcon />}
              </ClickableIcon>
            </div>
          </div>
          {isFlex && (
            <div className="mt-4">
              <div className="flex flex-col justify-center gap-x-3 gap-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <AlignField
                    flexDirectionState={flexDirectionState}
                    justifyContentState={justifyContentState}
                    alignItemsState={alignItemsState}
                    elementClasses={elementClasses}
                    tailwindClassesMap={tailwindClassesMap}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />

                  <FlexDirectionWrapField
                    flexWrapState={flexWrapState}
                    flexDirectionState={flexDirectionState}
                    justifyContentState={justifyContentState}
                    alignItemsState={alignItemsState}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />
                </div>

                {flexWrapState.propertyValue !== "nowrap" && (
                  <>
                    <AlignChildrenField
                      alignContentState={alignContentState}
                      flexDirectionState={flexDirectionState}
                      justifyContentState={justifyContentState}
                      alignItemsState={alignItemsState}
                      pickTailwindClassForProperty={
                        pickTailwindClassForProperty
                      }
                      clearTailwindClassForProperty={
                        clearTailwindClassForProperty
                      }
                    />
                  </>
                )}

                <WrapContentField
                  flexDirectionState={flexDirectionState}
                  justifyContentState={justifyContentState}
                  alignItemsState={alignItemsState}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  clearTailwindClassForProperty={clearTailwindClassForProperty}
                />

                <GapField
                  columnGapState={columnGapState}
                  rowGapState={rowGapState}
                  flexDirectionState={flexDirectionState}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  clearTailwindClassForProperty={clearTailwindClassForProperty}
                  updateInstantForProperty={updateInstantForProperty}
                />
              </div>
            </div>
          )}
        </div>
        <hr />
        <div className="px-4 py-2">
          <div className="flex flex-col">
            <div className="flex flex-row justify-between items-center">
              <div
                className={cn(
                  "text-xs font-medium",
                  !showStyleSection && "text-muted-foreground"
                )}
              >
                Styles
              </div>
              <div
                className={"flex flex-col items-center justify-center w-1/12"}
                onClick={() => {
                  setShowStyleSection((prev) => !prev);
                }}
              >
                <ClickableIcon>
                  {showStyleSection ? <MinusIcon /> : <PlusIcon />}
                </ClickableIcon>
              </div>
            </div>
            {showStyleSection && (
              <div className=" items-center gap-x-3 gap-y-2 mt-4">
                <div ref={fillModalRef}>
                  {!fillSet ? (
                    <EmptyInputState
                      onClick={() =>
                        setModal({
                          type: StylingModalType.Fill,
                          anchor: fillModalRef,
                        })
                      }
                    />
                  ) : (
                    <div
                      className="cursor-pointer flex flex-row items-center relative border bg-secondary-n-950 rounded-md h-7 px-2 hover:border-secondary-600 border-transparent"
                      onClick={() => {
                        setModal({
                          type: StylingModalType.Fill,
                          anchor: fillModalRef,
                        });
                      }}
                    >
                      {typeof cssValues["background-image"]?.evaluatedValue ===
                        "string" &&
                      cssValues["background-image"].evaluatedValue.includes(
                        "url"
                      ) ? (
                        <div className="flex items-center gap-2 w-full">
                          <div
                            className="w-4 h-4 rounded-xs bg-cover bg-center bg-no-repeat"
                            style={{
                              backgroundImage:
                                cssValues["background-image"]?.evaluatedValue,
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                            }}
                          />
                          <div className="text-xs font-medium text-secondary-n-50 truncate">
                            Image
                          </div>
                        </div>
                      ) : (
                        <>
                          <ColorSwatch
                            color={
                              typeof cssValues["background-image"]
                                ?.evaluatedValue === "string" &&
                              (cssValues[
                                "background-image"
                              ].evaluatedValue.includes("linear-gradient") ||
                                cssValues[
                                  "background-image"
                                ].evaluatedValue.includes("url"))
                                ? "#fff"
                                : cssValues["background-color"]
                                    ?.evaluatedValue || "#fff"
                            }
                            size="sm"
                            showBorder={false}
                          />
                          <div className="text-xs px-1.5 border-none outline-none w-full font-medium bg-transparent text-secondary-n-50 truncate max-w-[140px]">
                            {typeof cssValues["background-image"]
                              ?.evaluatedValue === "string" &&
                            cssValues[
                              "background-image"
                            ].evaluatedValue.includes("linear-gradient")
                              ? "Gradient"
                              : backgroundColorState.displayValue || "Solid"}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-xs text-secondary-n-400 mb-1 mt-2">
                  Rotate
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DraggableInput
                      placeholder="0°"
                      propertyState={rotationState}
                      labelContent={<AngleIcon />}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.ROTATE,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.ROTATE,
                          option,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `rotate-[${input}]`,
                          css: `transform: rotate(${input})`,
                        };
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.ROTATE,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      comboboxOptions={rotationProperties}
                      tooltipContent="Rotation"
                    />
                  </div>

                  <div className="flex gap-1">
                    <div
                      className={cn(
                        "flex items-center justify-center gap-1 bg-secondary-n-950 p-1 rounded-md cursor-pointer"
                      )}
                    >
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50"
                        )}
                        onClick={() => {
                          resetRotationAndFlips();
                          setRotationMode("");
                        }}
                        title="Reset Rotation"
                      >
                        <RevertIcon />
                      </Button>
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50",
                          elementClasses.includes("scale-x-[-1]") &&
                            "bg-secondary-n-900 text-secondary-n-50"
                        )}
                        onClick={() => {
                          toggleArbitraryClass(
                            "scale-x-[-1]",
                            "transform: scaleX(-1);"
                          );
                        }}
                        title="Flip Horizontally"
                      >
                        <FliPIcon className="rotate-90" />
                      </Button>
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50",
                          elementClasses.includes("scale-y-[-1]") &&
                            "bg-secondary-n-900 text-secondary-n-50"
                        )}
                        onClick={() => {
                          toggleArbitraryClass(
                            "scale-y-[-1]",
                            "transform: scaleY(-1);"
                          );
                        }}
                        title="Flip Vertically"
                      >
                        <FliPIcon />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-secondary-n-400 mt-2 mb-1">
                  Border
                </div>

                <div className="col-span-8" ref={borderModalRef}>
                  {borderSet ? (
                    <div
                      className="cursor-pointer flex flex-row items-center relative border bg-secondary-n-950 rounded-md h-7 px-2 hover:border-secondary-600 border-transparent"
                      onClick={() => {
                        setModal({
                          type: StylingModalType.Border,
                          anchor: borderModalRef,
                        });
                      }}
                    >
                      <ColorSwatch
                        color={
                          cssValues["border-color"]?.evaluatedValue ||
                          borderColorState.propertyValue ||
                          "#fff"
                        }
                        size="sm"
                        showBorder={false}
                      />
                      <div className="text-2xs px-1 border-none outline-none w-full font-medium bg-transparent text-white">
                        {borderColorState.displayValue || "Solid"}
                      </div>
                    </div>
                  ) : (
                    <EmptyInputState
                      label="Add border"
                      onClick={() => {
                        setModal({
                          type: StylingModalType.Border,
                          anchor: borderModalRef,
                        });
                      }}
                    />
                  )}

                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1">
                      <div className="col-span-2">
                        <DraggableInput
                          placeholder="0px"
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.BORDER_WIDTH,
                              option,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          labelContent={<SpacingIcon />}
                          min={0}
                          step={0.25}
                          propertyState={borderWidthState}
                          buildArbitraryClass={(input) => ({
                            tailwind: `border-[${input}]`,
                            css: `border-width: ${input};`,
                          })}
                          comboboxOptions={borderWidthProperties}
                          tooltipContent="Border width"
                          onComboboxSelected={(option, variant) => {
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.BORDER_WIDTH,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            });
                          }}
                          disabled={borderWidthExpanded}
                          onClear={(variant?: string) => {
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.BORDER_WIDTH,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <DraggableInput
                        placeholder="Solid"
                        labelContent={<SolidBarIcon />}
                        draggable={false}
                        onSliderValueUpdate={(option, variant) =>
                          updateInstantForProperty({
                            propertyType: PropertyType.BORDER_STYLE,
                            option,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          })
                        }
                        propertyState={borderStyleState}
                        comboboxOptions={borderStyleProperties}
                        tooltipContent="Border style"
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.BORDER_STYLE,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.BORDER_STYLE,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row items-center gap-x-2 mt-2">
                  <div className="flex-1">
                    <DraggableInput
                      propertyState={borderRadiusState}
                      labelContent={<SquareIcon className="w-4 h-4" />}
                      disabled={radiusExpanded}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_RADIUS,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_RADIUS,
                          option,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      comboboxOptions={borderRadiusProperties}
                      buildArbitraryClass={(input) => {
                        return {
                          tailwind: `rounded-[${input}]`,
                          css: `border-top-left-radius: ${input}; border-top-right-radius: ${input}; border-bottom-left-radius: ${input}; border-bottom-right-radius: ${input}`,
                        };
                      }}
                      tooltipContent="Border radius"
                      onClear={(variant?: string) =>
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_RADIUS,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                    />
                  </div>

                  <div
                    className={cn(
                      "flex items-center justify-center gap-1 bg-secondary-n-950 p-1 rounded-md cursor-pointer",
                      radiusExpanded
                        ? "bg-secondary-n-900 text-secondary-n-50"
                        : "bg-secondary-n-950 text-secondary-n-600"
                    )}
                    onClick={() => setRadiusExpanded((prev) => !prev)}
                  >
                    <Scan className="w-5 h-5 p-[3px]" />
                  </div>
                </div>

                {/* Individual Radius */}
                {radiusExpanded && (
                  <div className="mt-2">
                    <div className="col-span-8">
                      <div className="grid grid-cols-2 gap-2 gap-x-2 border border-transparent">
                        <div>
                          <DraggableInput
                            labelContent={<CornerTopLeftIcon />}
                            propertyState={topLeftRadiusState}
                            onInputFocus={() => setActiveRadius(0)}
                            comboboxOptions={topLeftRadiusProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-tl-[${input}]`,
                                css: `border-top-left-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Top left radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerTopRightIcon />}
                            propertyState={topRightRadiusState}
                            onInputFocus={() => setActiveRadius(1)}
                            comboboxOptions={topRightRadiusProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-tr-[${input}]`,
                                css: `border-top-right-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Top right radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerBottomLeftIcon />}
                            propertyState={bottomLeftRadiusState}
                            onInputFocus={() => setActiveRadius(3)}
                            comboboxOptions={bottomLeftRadiusProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-bl-[${input}]`,
                                css: `border-bottom-left-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Bottom left radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerBottomRightIcon />}
                            propertyState={bottomRightRadiusState}
                            onInputFocus={() => setActiveRadius(2)}
                            comboboxOptions={bottomRightRadiusProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-br-[${input}]`,
                                css: `border-bottom-right-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Bottom right radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-col">
            <div className="flex flex-row justify-between items-center">
              <h1
                className={cn(
                  "text-xs font-medium",
                  !showTextSection && "text-muted-foreground"
                )}
              >
                Typography
              </h1>
              <div
                className={"flex flex-col items-center justify-center w-1/12"}
                onClick={() => {
                  setShowTextSection((prev) => !prev);
                }}
              >
                <ClickableIcon>
                  {showTextSection ? <MinusIcon /> : <PlusIcon />}
                </ClickableIcon>
              </div>
            </div>
            {showTextSection && (
              <div className=" items-center  gap-x-2 gap-y-2 mt-4">
                <DraggableInput
                  draggable={false}
                  propertyState={fontFamilyState}
                  labelContent={<Type className="w-4 h-4" />}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `font-[${input}]`,
                      css: `font-family: ${input};`,
                    };
                  }}
                  comboboxOptions={fontFamilyProperties}
                  onComboboxSelected={(option, variant) =>
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FONT_FAMILY,
                      setTemporaryDraggingValue:
                        fontFamilyState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  tooltipContent="Font family"
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FONT_FAMILY,
                      setTemporaryDraggingValue:
                        fontFamilyState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />

                <div className="grid grid-cols-2 gap-2 mt-2">
                  <DraggableInput
                    propertyState={fontWeightState}
                    labelContent={
                      <Type strokeWidth={3.5} className="w-4 h-4" />
                    }
                    min={100}
                    max={900}
                    step={100}
                    tooltipContent="Font weight"
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `font-[${input}]`,
                        css: `font-weight: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.FONT_WEIGHT,
                        option,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    buildOptionPreview={buildFontWeightColorPreview}
                    comboboxOptions={fontWeightProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.FONT_WEIGHT,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FONT_WEIGHT,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={fontSizeState}
                    labelContent={
                      <FontSizeIcon className="transform -scale-x-100" />
                    }
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `text-[${input}]`,
                        css: `font-size: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.FONT_SIZE,
                        option,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    comboboxOptions={fontSizeProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.FONT_SIZE,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    tooltipContent="Font size"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FONT_SIZE,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={letterSpacingState}
                    labelContent={<LetterSpacingIcon />}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `tracking-[${input}]`,
                        css: `letter-spacing: ${input};`,
                      };
                    }}
                    comboboxOptions={letterSpacingProperties}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.LETTER_SPACING,
                        option,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.LETTER_SPACING,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    step={0.05}
                    tooltipContent="Letter spacing"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.LETTER_SPACING,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={lineHeightState}
                    labelContent={<LineHeightIcon />}
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `leading-[${input}]`,
                        css: `line-height: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.LINE_HEIGHT,
                        option,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    comboboxOptions={lineHeightProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.LINE_HEIGHT,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    tooltipContent="Line height"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.LINE_HEIGHT,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <div ref={textColorModalRef}>
                    {textColorState.isSet ? (
                      <div
                        className="cursor-pointer flex flex-row items-center relative border bg-secondary-200 rounded-md h-7 px-2 hover:border-secondary-600 border-transparent"
                        onClick={() => {
                          setModal({
                            type: StylingModalType.TextColor,
                            anchor: textColorModalRef,
                          });
                        }}
                      >
                        <ColorSwatch
                          color={textColorDisplayHex || "#fff"}
                          size="sm"
                          showBorder={false}
                        />
                        <div className="text-xs px-1 border-none outline-none w-full font-medium bg-transparent text-secondary-n-50">
                          {textColorState.displayValue || "Solid"}
                        </div>
                      </div>
                    ) : (
                      <EmptyInputState
                        onClick={() => {
                          setModal({
                            type: StylingModalType.TextColor,
                            anchor: textColorModalRef,
                          });
                        }}
                        label="Text color"
                      />
                    )}
                  </div>

                  <TextAlignField
                    textAlignState={textAlignState}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />

                  <TextDecorationField
                    textDecorationLineState={textDecorationLineState}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <hr />
        <div className="px-4 py-2">
          <div className="flex flex-col w-full space-y-2">
            <div className="flex flex-row justify-between items-center">
              <h1
                className={cn(
                  "text-xs font-semibold",
                  !showEffectSection && "text-muted-foreground"
                )}
              >
                Effects
              </h1>
              <div
                className="flex flex-col items-center justify-center w-1/12"
                onClick={() => {
                  setShowEffectSection((prev) => !prev);
                }}
              >
                <ClickableIcon>
                  {showEffectSection ? <MinusIcon /> : <PlusIcon />}
                </ClickableIcon>
              </div>
            </div>
            {showEffectSection && (
              <div className="grid grid-cols-7 items-center pl-2 pr-2 gap-x-3 gap-y-2">
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Animation
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={animationState}
                      labelContent={<MagicWandIcon />}
                      comboboxOptions={animationProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.ANIMATION,
                          setTemporaryDraggingValue:
                            animationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      draggable={false}
                      tooltipContent="Animation"
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.ANIMATION,
                          setTemporaryDraggingValue:
                            animationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Shadow
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={boxShadowState}
                      labelContent={<ShadowIcon />}
                      comboboxOptions={boxShadowProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BOX_SHADOW,
                          setTemporaryDraggingValue:
                            boxShadowState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BOX_SHADOW,
                          setTemporaryDraggingValue:
                            boxShadowState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      draggable={false}
                      tooltipContent="Shadow"
                    />
                  </div>
                </div>
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Blur
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={blurState}
                      labelContent={<BlendingModeIcon />}
                      comboboxOptions={blurProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BLUR,
                          setTemporaryDraggingValue:
                            blurState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BLUR,
                          setTemporaryDraggingValue:
                            blurState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      draggable={false}
                      tooltipContent="Blur"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* <DebugInformation
          cssValues={cssValues}
          selectedElement={selectedElement}
          selectedTreeElement={selectedTreeElement}
          elementClassList={elementClassList || []}
          parentCssEvals={parentCssEvals || {}}
        /> */}
      </ComboboxContainer>
      <ModalContainer
        className={cn("!w-[250px] !border-none !gap-y-3")}
        title={modalTypeToTitle[modal.type]}
        visible={modal.type !== StylingModalType.None}
        onDismiss={() => setModal({ type: StylingModalType.None })}
        anchorRef={modal.anchor}
        containerRef={containerRef}
      >
        <ComboboxContainer>
          {modal.type === StylingModalType.TextColor && (
            <div className="grid grid-cols-1">
              <div style={{ position: "relative" }}>
                <CustomColorPicker
                  propertyState={textColorState}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `text-[${input}]`,
                      css: `color: ${input};`,
                    };
                  }}
                  instantUpdateValue={(option) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      option,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                    })
                  }
                  comboboxOptions={sortTailwindColorOptions(
                    textColorProperties
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                    });
                  }}
                  buildOptionPreview={buildTextColorPreview}
                  tooltipContent="Text color"
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                  onOpenVariantModal={() => setTextColorVariantModalOpen(true)}
                />
                {/* Hidden button for ChevronDown anchor */}
                <button
                  type="button"
                  ref={textColorVariantAnchorRef}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  tabIndex={-1}
                  aria-hidden
                />
              </div>
            </div>
          )}
          {modal.type === StylingModalType.Border && (
            <div className="grid grid-cols-8 items-center gap-x-1 gap-y-2">
              <div className="col-span-8">
                <div style={{ position: "relative" }}>
                  <CustomColorPicker
                    propertyState={borderColorState}
                    buildArbitraryClass={(input) => {
                      return {
                        tailwind: `border-[${input}]`,
                        css: `border-color: ${input};`,
                      };
                    }}
                    instantUpdateValue={(option) => {
                      const hasBorderWidth = elementClasses.some(
                        (cls) =>
                          cls.startsWith("border-") &&
                          !cls.startsWith("border-[") &&
                          !cls.startsWith("border-color") &&
                          !cls.startsWith("border-style")
                      );
                      if (!hasBorderWidth) {
                        pickTailwindClassForProperty({
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          propertyType: PropertyType.BORDER_WIDTH,
                          setTemporaryDraggingValue:
                            borderWidthState.setTemporaryDraggingValue,
                        });
                      }
                      updateInstantForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        option,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    comboboxOptions={sortTailwindColorOptions(
                      borderColorProperties
                    )}
                    setValue={(option) => {
                      const hasBorderWidth = elementClasses.some(
                        (cls) =>
                          cls.startsWith("border-") &&
                          !cls.startsWith("border-[") &&
                          !cls.startsWith("border-color") &&
                          !cls.startsWith("border-style")
                      );
                      if (!hasBorderWidth) {
                        pickTailwindClassForProperty({
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          propertyType: PropertyType.BORDER_WIDTH,
                          setTemporaryDraggingValue:
                            borderWidthState.setTemporaryDraggingValue,
                        });
                      }
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    tooltipContent="Border color"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    showHex={true}
                    onOpenVariantModal={() =>
                      setBorderColorVariantModalOpen(true)
                    }
                  />
                  {/* Hidden button for ChevronDown anchor */}
                  <button
                    type="button"
                    ref={borderColorVariantAnchorRef}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      width: 1,
                      height: 1,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                    tabIndex={-1}
                    aria-hidden
                  />
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-secondary-800 font-medium">
                  Style
                </div>
              </div>
              <div className="col-span-6">
                <DraggableInput
                  labelContent={<BorderStyleIcon />}
                  draggable={false}
                  onSliderValueUpdate={(option, variant) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.BORDER_STYLE,
                      option,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  propertyState={borderStyleState}
                  comboboxOptions={borderStyleProperties}
                  tooltipContent="Border style"
                  onComboboxSelected={(option, variant) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_STYLE,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_STYLE,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />
              </div>
              <div className="col-span-2">
                <div className="text-xs text-secondary-800 font-medium">
                  Width
                </div>
              </div>
              <div className="col-span-3">
                <DraggableInput
                  onSliderValueUpdate={(option, variant) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.BORDER_WIDTH,
                      option,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  labelContent={
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      className="bi bi-border-width rotate-180"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 3.5A.5.5 0 0 1 .5 3h15a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-2zm0 5A.5.5 0 0 1 .5 8h15a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-1zm0 4a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z" />
                    </svg>
                  }
                  min={0}
                  step={0.25}
                  propertyState={borderWidthState}
                  buildArbitraryClass={(input) => ({
                    tailwind: `border-[${input}]`,
                    css: `border-width: ${input};`,
                  })}
                  comboboxOptions={borderWidthProperties}
                  tooltipContent="Border width"
                  onComboboxSelected={(option, variant) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_WIDTH,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  disabled={borderWidthExpanded}
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_WIDTH,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />
              </div>
              <div className="col-span-3">
                <DiscreteInput
                  options={BORDER_WIDTH_OPTIONS}
                  selected={borderWidthExpanded ? "individual" : "all"}
                  onOptionSelected={(option) => {
                    if (option.key === "all") {
                      if (borderWidthExpanded) {
                        setBorderWidthExpanded(false);
                        // setBorderWidth(borderLeftWidth);
                        // changeBorderWidth({
                        //   "border-width": borderLeftWidth,
                        // });
                      }
                    } else {
                      if (!borderWidthExpanded) {
                        setBorderWidthExpanded(true);
                      }
                    }
                  }}
                  set={false}
                />
              </div>
              {borderWidthExpanded && (
                <>
                  <div className="col-span-3 col-start-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderLeftIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderLeftWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-l-[${input}]`,
                        css: `border-left-width: ${input};`,
                      })}
                      tooltipContent="Border left width"
                      comboboxOptions={borderLeftWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderRightIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderRightWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-r-[${input}]`,
                        css: `border-right-width: ${input};`,
                      })}
                      tooltipContent="Border right width"
                      comboboxOptions={borderRightWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3 col-start-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderBottomIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderBottomWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-b-[${input}]`,
                        css: `border-bottom-width: ${input};`,
                      })}
                      tooltipContent="Border bottom width"
                      comboboxOptions={borderBottomWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderTopIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderTopWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-t-[${input}]`,
                        css: `border-top-width: ${input};`,
                      })}
                      tooltipContent="Border top width"
                      comboboxOptions={borderTopWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {modal.type === StylingModalType.Fill && (
            <FillModalV2
              pickTailwindClassForProperty={pickTailwindClassForProperty}
              updateInstantForProperty={updateInstantForProperty}
              removeClassForProperty={clearTailwindClassForProperty}
              removeClassesForMultipleProperties={
                clearTailwindClassForMultipleProperties
              }
              cssValues={cssValues}
              selectedTreeElement={selectedTreeElement}
              selectedElement={selectedElement}
              onOpenFillVariantModal={() => setFillColorVariantModalOpen(true)}
              fillVariantAnchorRef={fillColorVariantAnchorRef}
              modalPopoverRef={fillModalPopoverRef}
              onOpenFromVariantModal={() => setFromColorVariantModalOpen(true)}
              fromVariantAnchorRef={fromColorVariantAnchorRef}
              onOpenViaVariantModal={() => setViaColorVariantModalOpen(true)}
              viaVariantAnchorRef={viaColorVariantAnchorRef}
              onOpenToVariantModal={() => setToColorVariantModalOpen(true)}
              toVariantAnchorRef={toColorVariantAnchorRef}
            />
          )}
        </ComboboxContainer>
      </ModalContainer>
      {textColorVariantModalOpen && (
        <VariantControl
          valueByVariant={textColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = textColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.TEXT_COLOR,
                setTemporaryDraggingValue:
                  textColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setTextColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.TEXT_COLOR,
              setTemporaryDraggingValue:
                textColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={textColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={textColorVariantModalOpen}
          setModalOpen={setTextColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            // Build a propertyState for the selected variant
            const variantValue =
              textColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              textColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!textColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^text-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...textColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `text-[${input}]`,
                      css: `color: ${input};`,
                    };
                  }}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    textColorProperties
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  buildOptionPreview={buildTextColorPreview}
                  tooltipContent="Text color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {borderColorVariantModalOpen && (
        <VariantControl
          valueByVariant={borderColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = borderColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.BORDER_COLOR,
                setTemporaryDraggingValue:
                  borderColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setBorderColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.BORDER_COLOR,
              setTemporaryDraggingValue:
                borderColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={borderColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={borderColorVariantModalOpen}
          setModalOpen={setBorderColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            // Build a propertyState for the selected variant
            const variantValue =
              borderColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              borderColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!borderColorState.valueByVariant[variant]?.active;
            // Use displayValue if it's a valid hex, otherwise extract from Tailwind class
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^border-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...borderColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `border-[${input}]`,
                      css: `border-color: ${input};`,
                    };
                  }}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    borderColorProperties
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Border color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {fillColorVariantModalOpen && (
        <VariantControl
          valueByVariant={backgroundColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = backgroundColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.BACKGROUND_COLOR,
                setTemporaryDraggingValue:
                  backgroundColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setFillColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.BACKGROUND_COLOR,
              setTemporaryDraggingValue:
                backgroundColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={fillColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={fillColorVariantModalOpen}
          setModalOpen={setFillColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              backgroundColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              backgroundColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!backgroundColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^bg-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...backgroundColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `bg-[${input}]`,
                    css: `background-color: ${input}`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    backgroundColorProperties
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Background color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {fromColorVariantModalOpen && (
        <VariantControl
          valueByVariant={fromColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = fromColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.FROM_COLOR,
                setTemporaryDraggingValue:
                  fromColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setFromColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.FROM_COLOR,
              setTemporaryDraggingValue:
                fromColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={fromColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={fromColorVariantModalOpen}
          setModalOpen={setFromColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            let variantValue =
              fromColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              fromColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!fromColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^from-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...fromColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `from-[${input}]`,
                    css: `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    fromColorProperties
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="From color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {viaColorVariantModalOpen && (
        <VariantControl
          valueByVariant={viaColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = viaColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.VIA_COLOR,
                setTemporaryDraggingValue:
                  viaColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setViaColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.VIA_COLOR,
              setTemporaryDraggingValue:
                viaColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={viaColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={viaColorVariantModalOpen}
          setModalOpen={setViaColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              viaColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              viaColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!viaColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^via-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...viaColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `via-[${input}]`,
                    css: `--tw-gradient-stops: var(--tw-gradient-from), ${input} var(--tw-gradient-via-position), var(--tw-gradient-to);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(viaColorProperties)}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Via color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {toColorVariantModalOpen && (
        <VariantControl
          valueByVariant={toColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = toColorProperties.find(
              (opt) => opt.tailwind === value
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.TO_COLOR,
                setTemporaryDraggingValue:
                  toColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setToColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.TO_COLOR,
              setTemporaryDraggingValue: toColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={toColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={toColorVariantModalOpen}
          setModalOpen={setToColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            let variantValue =
              toColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              toColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet = !!toColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^to-\[([#a-fA-F0-9]+)\]$/
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...toColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `to-[${input}]`,
                    css: `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(toColorProperties)}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="To color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
    </div>
  );
};
